import { Vector3 } from "three";
import type { PerspectiveCamera } from "three";
import { config } from "../../../config";
import { clamp, damp } from "../../../utils/math";
import { GroundRay } from "../GroundRay";
import type { CameraController } from "../../../types/camera";
import type { ReadonlyInputState } from "../../../types/input";

export interface OrbitCameraOptions {
  /** Point observé. Fixe par défaut ; `pivot` se déplace au clavier ou au doigt. */
  pivot?: Vector3;
  distance?: number;
  /** Tangage initial en radians. Au-dessus du plan XZ. */
  pitch?: number;
}

/**
 * Caméra orbitale écrite à la main plutôt qu'avec OrbitControls, qui écoute
 * le DOM en direct et court-circuiterait InputManager (donc le tactile).
 *
 * Repère : yaw autour de Y, pitch au-dessus du plan XZ, distance en mètres.
 */
export class OrbitCameraController implements CameraController {
  readonly name = "orbit";

  private readonly pivot: Vector3;
  private readonly smoothedPivot: Vector3;
  private readonly offset = new Vector3();
  /** Ancrage du zoom : le rayon du curseur, l'œil visé, le point au sol. */
  private readonly ray: GroundRay;
  private readonly eye = new Vector3();
  private readonly anchor = new Vector3();
  /** Emprise de la carte. Infinie tant que personne ne l'a donnée. */
  private minX = -Infinity;
  private maxX = Infinity;
  private minZ = -Infinity;
  private maxZ = Infinity;

  private targetYaw = 0;
  private targetPitch: number;
  private targetDistance: number;
  private yaw = 0;
  private pitch: number;
  private distance: number;

  constructor(
    private readonly camera: PerspectiveCamera,
    options: OrbitCameraOptions = {},
  ) {
    this.pivot = options.pivot?.clone() ?? new Vector3(0, 0, 0);
    this.smoothedPivot = this.pivot.clone();
    this.targetDistance = options.distance ?? config.camera.distance;
    this.distance = this.targetDistance;
    this.targetPitch = options.pitch ?? config.camera.pitch;
    this.pitch = this.targetPitch;
    this.ray = new GroundRay(camera);
    this.apply();
  }

  /** Recadre immédiatement, sans amortissement : c'est un saut, pas un geste. */
  setDistance(distance: number): void {
    this.targetDistance = clamp(
      distance,
      config.camera.minDistance,
      config.camera.maxDistance,
    );
    this.distance = this.targetDistance;
  }

  /**
   * Oriente la vue, en radians autour de Y ([GD-046]).
   *
   * Amorti par défaut : la caméra pivote, elle ne saute pas — un demi-tour
   * annonce le changement de camp mieux qu'une coupure. La cible est ramenée
   * au tour le plus court depuis l'orientation courante, sans quoi une caméra
   * déjà tournée à la main ferait un tour complet pour rien.
   */
  turnTo(yaw: number, instant = false): void {
    const turn = Math.PI * 2;
    const delta =
      ((((yaw - this.yaw + Math.PI) % turn) + turn) % turn) - Math.PI;
    this.targetYaw = this.yaw + delta;
    if (instant) this.yaw = this.targetYaw;
  }

  /**
   * Emprise de la carte en mètres. Le point observé n'en sort que d'une marge
   * proportionnelle à la distance, et le zoom ne vise jamais au-delà
   * ([GD-019]). Sans appel, rien n'est borné.
   */
  setBounds(bounds: {
    readonly minX: number;
    readonly maxX: number;
    readonly minZ: number;
    readonly maxZ: number;
  }): void {
    this.minX = bounds.minX;
    this.maxX = bounds.maxX;
    this.minZ = bounds.minZ;
    this.maxZ = bounds.maxZ;
  }

  update(input: ReadonlyInputState, dt: number): void {
    const { camera: settings } = config;

    // Les axes sont inversés pour que le monde suive le doigt (ou le curseur),
    // convention habituelle d'une caméra orbitale.
    this.targetYaw -= input.look.x * settings.lookSpeed * dt;
    this.targetPitch = clamp(
      this.targetPitch - input.look.y * settings.lookSpeed * dt,
      settings.minPitch,
      settings.maxPitch,
    );
    // La distance minimale dépend du tangage : à angle rasant, se rapprocher
    // fait descendre la caméra. On borne le zoom plutôt que de redresser la
    // vue, un zoom qui s'arrête étant plus prévisible qu'un angle qui bouge
    // tout seul ([TD-040]).
    const floorDistance =
      settings.minAltitude / Math.sin(Math.max(this.targetPitch, 1e-3));
    const previousDistance = this.targetDistance;
    this.targetDistance = clamp(
      this.targetDistance - input.zoom * settings.zoomSpeed * dt,
      Math.max(settings.minDistance, floorDistance),
      settings.maxDistance,
    );

    this.anchorZoom(input, previousDistance);
    this.pan(input, dt);
    this.clampPivot();

    this.yaw = damp(this.yaw, this.targetYaw, settings.damping, dt);
    this.pitch = damp(this.pitch, this.targetPitch, settings.damping, dt);
    this.distance = damp(
      this.distance,
      this.targetDistance,
      settings.damping,
      dt,
    );
    this.dampTo(this.smoothedPivot, this.pivot, dt);

    this.apply();
  }

  /** Amortit un point vers un autre, composante par composante. */
  private dampTo(out: Vector3, target: Vector3, dt: number): void {
    const { damping } = config.camera;
    out.x = damp(out.x, target.x, damping, dt);
    out.y = damp(out.y, target.y, damping, dt);
    out.z = damp(out.z, target.z, damping, dt);
  }

  /**
   * Garde sous le curseur le point du sol qui s'y trouvait avant le zoom
   * ([GD-017]). Sans cela le zoom converge toujours vers le centre de l'écran.
   *
   * Pour qu'un point du plan reste au même endroit à l'écran quand la distance
   * passe de `d0` à `d1`, il suffit de rapprocher le pivot de ce point dans le
   * rapport `1 - d1/d0` : les deux sont à la même altitude, l'orientation ne
   * bouge pas, tout le reste se simplifie.
   *
   * Vrai du curseur comme du pincement : `zoomAnchored` dit qu'il y a un
   * point de convergence, et `pointer` où il est ([GD-043]). Sans ancre — au
   * clavier, ou curseur sorti de la fenêtre — le zoom reste centré.
   */
  private anchorZoom(
    input: ReadonlyInputState,
    previousDistance: number,
  ): void {
    if (!input.zoomAnchored || this.targetDistance === previousDistance) return;
    const seen = this.ray.hit(
      input.pointer,
      this.eyeAt(previousDistance),
      this.pivot,
      this.pivot.y,
      this.anchor,
    );
    if (!seen) return;

    // Le curseur sort de la carte : on vise le bord, jamais le vide ([GD-019]).
    const x = clamp(this.anchor.x, this.minX, this.maxX);
    const z = clamp(this.anchor.z, this.minZ, this.maxZ);
    const closing = 1 - this.targetDistance / previousDistance;
    this.pivot.x += (x - this.pivot.x) * closing;
    this.pivot.z += (z - this.pivot.z) * closing;
  }

  /**
   * Retient le point observé à portée de la carte. La marge suit la distance :
   * dégager la vue reste possible, perdre la carte de vue non ([GD-019]).
   */
  private clampPivot(): void {
    const margin = this.targetDistance * config.camera.edgeMargin;
    this.pivot.x = clamp(this.pivot.x, this.minX - margin, this.maxX + margin);
    this.pivot.z = clamp(this.pivot.z, this.minZ - margin, this.maxZ + margin);
  }

  /** Position visée de l'œil à cette distance, avant amortissement. */
  private eyeAt(distance: number): Vector3 {
    return this.offsetFor(
      this.targetYaw,
      this.targetPitch,
      distance,
      this.eye,
    ).add(this.pivot);
  }

  /** Écart entre le pivot et l'œil : la seule trigonométrie de la caméra. */
  private offsetFor(
    yaw: number,
    pitch: number,
    distance: number,
    out: Vector3,
  ): Vector3 {
    const cosPitch = Math.cos(pitch);
    return out.set(
      distance * cosPitch * Math.sin(yaw),
      distance * Math.sin(pitch),
      distance * cosPitch * Math.cos(yaw),
    );
  }

  /**
   * Déplace le point observé dans le plan horizontal, relativement à la vue.
   *
   * La vitesse suit la distance : de près on ajuste au mètre, de loin on
   * traverse la carte. Une vitesse fixe rend l'un des deux inutilisable — et
   * c'est de près que ça se voit ([GD-044]).
   */
  private pan(input: ReadonlyInputState, dt: number): void {
    if (input.move.x === 0 && input.move.y === 0) return;
    const scale = this.distance / config.camera.distance;
    const speed = config.camera.panSpeed * scale * dt;
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    this.pivot.x += (input.move.x * cos - input.move.y * sin) * speed;
    this.pivot.z += (-input.move.x * sin - input.move.y * cos) * speed;
  }

  private apply(): void {
    this.offsetFor(this.yaw, this.pitch, this.distance, this.offset);
    // Filet de sécurité : l'amortissement passe par des états intermédiaires
    // que les bornes sur les cibles ne couvrent pas toutes.
    const lowest = config.camera.minAltitude - this.smoothedPivot.y;
    if (this.offset.y < lowest) this.offset.y = lowest;

    this.camera.position.copy(this.smoothedPivot).add(this.offset);
    this.camera.lookAt(this.smoothedPivot);
  }

  /** Rien à libérer : ni écouteur DOM, ni ressource GPU. Présent par contrat. */
  dispose(): void {}
}
